import React, { useEffect } from 'react';
import { Input } from '../../../components/common/Form';
import { STATES } from '../../../constants/StatesConstants';
import { useCuisinesContext } from '../../../contexts/CuisinesContext';

export interface BrandBasicInfoData {
  brandName: string;
  cuisineType: string;
  streetAddress1: string;
  streetAddress2: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  email: string;
}

interface BrandBasicInfoStepProps {
  data: BrandBasicInfoData;
  onChange: (data: BrandBasicInfoData) => void;
  onNext: () => void;
  onCancel: () => void;
}

const INPUT_OVERRIDE = `
  .brand-wizard-form input,
  .brand-wizard-form select {
    padding: 10px 12px !important;
    font-size: 0.8125rem !important;
    height: 40px !important;
    box-sizing: border-box !important;
  }
`;

const BrandBasicInfoStep = ({ data, onChange, onNext, onCancel }: BrandBasicInfoStepProps) => {
  const updateField = (field: keyof BrandBasicInfoData, value: string) => {
    onChange({ ...data, [field]: value });
  };

  const { cuisines, loadCuisines } = useCuisinesContext();

  useEffect(() => {
    loadCuisines();
  }, [loadCuisines]);

  return (
    <div className="brand-wizard-form" style={{ width: '100%' }}>
      <style>{INPUT_OVERRIDE}</style>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px' }}>
        <Input
          name="brandName"
          label="BRAND NAME"
          value={data.brandName}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateField('brandName', e.target.value)}
        />
        <div>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label className="font-semibold text-sm tracking-[1.1375px]" htmlFor="cuisineType">
            CUISINE TYPE
          </label>
          <div className="mt-2">
            <select
              id="cuisineType"
              name="cuisineType"
              value={data.cuisineType}
              onChange={(e) => updateField('cuisineType', e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                fontSize: '0.8125rem',
                borderRadius: '2px',
                border: '1px solid #a3a3a3',
                backgroundColor: 'white',
                height: '40px',
                boxSizing: 'border-box'
              }}
            >
              <option value="">Select</option>
              {cuisines.map((cuisine) => (
                <option value={cuisine.name} key={cuisine.cuisineID}>
                  {cuisine.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px', marginTop: '16px' }}>
        <Input
          name="streetAddress1"
          label="STREET ADDRESS 1"
          value={data.streetAddress1}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateField('streetAddress1', e.target.value)}
        />
        <Input
          name="streetAddress2"
          label="STREET ADDRESS 2"
          value={data.streetAddress2}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateField('streetAddress2', e.target.value)}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '32px', marginTop: '16px' }}>
        <Input
          name="city"
          label="CITY"
          value={data.city}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateField('city', e.target.value)}
        />
        <div>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label className="font-semibold text-sm tracking-[1.1375px]" htmlFor="brandState">
            STATE
          </label>
          <div className="mt-2">
            <select
              id="brandState"
              name="state"
              value={data.state}
              onChange={(e) => updateField('state', e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                fontSize: '0.8125rem',
                borderRadius: '2px',
                border: '1px solid #a3a3a3',
                backgroundColor: 'white',
                height: '40px',
                boxSizing: 'border-box'
              }}
            >
              <option value="">Select</option>
              {STATES.map((state: { label: string; value: string }) => (
                <option value={state.value} key={state.value}>
                  {state.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <Input
          name="zip"
          label="ZIP"
          value={data.zip}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateField('zip', e.target.value)}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px', marginTop: '16px' }}>
        <Input
          name="phone"
          label="PHONE NUMBER"
          value={data.phone}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateField('phone', e.target.value)}
        />
        <Input
          name="email"
          label="EMAIL ADDRESS"
          value={data.email}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => updateField('email', e.target.value)}
        />
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '24px' }}>
        <button
          type="button"
          onClick={onCancel}
          style={{
            width: '167px',
            height: '35px',
            backgroundColor: 'var(--color-danger)',
            color: 'white',
            borderRadius: '999px',
            border: 'none',
            fontSize: '0.75rem',
            fontWeight: 600,
            letterSpacing: '1.4px',
            cursor: 'pointer'
          }}
        >
          CANCEL
        </button>
        <button
          type="button"
          onClick={onNext}
          style={{
            width: '167px',
            height: '35px',
            backgroundColor: 'var(--color-accent)',
            color: 'white',
            borderRadius: '999px',
            border: 'none',
            fontSize: '0.75rem',
            fontWeight: 600,
            letterSpacing: '1.4px',
            cursor: 'pointer'
          }}
        >
          NEXT
        </button>
      </div>
    </div>
  );
};

export default BrandBasicInfoStep;
